import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import config from '@/config'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import Avatar from '@/modules/base/components/avatar/avatar'
import buildName from '../_helpers/buildName'
import clsx from 'clsx'
import TodoHistoryTypes from '@/enums/todoHistoryTypes.enum'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import DeleteIcon from '@assets/images/icons/trashcan.svg'
import { user } from 'ampersand-app'
import TodoStatusEnum from '@/enums/TodoStatus.enum'
import CheckIcon from '@assets/images/icons/check.svg'
import CloseIcon from '@assets/images/icons/close.svg'
import SigningIcon from '@assets/images/icons/signature_pen.svg'
import theme from '@/theme'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  container: ({ isMessage }) => ({
    display: 'flex',
    flexDirection: `column${!isMessage ? '-reverse' : ''}`,
    width: '100%',
  }),
  avatar: ({ isCreator, isMessage }) => ({
    display: isMessage ? 'block' : 'none',
    marginLeft: isCreator ? 10 : 0,
    marginRight: !isMessage || isCreator ? 0 : 10,
  }),
  meta: ({ isCreator, isMessage }) => ({
    display: isMessage ? 'flex' : 'block',
    flexDirection: isMessage && isCreator ? 'row-reverse' : 'row',
    textAlign: isMessage ? 'left' : 'center',
  }),
  message: ({ isCreator, isMessage, isDeleted }) => ({
    textAlign: isMessage ? (isCreator ? 'right' : 'left') : 'center',
    marginLeft: !isMessage || isCreator ? 0 : 30,
    marginRight: !isMessage || isCreator ? 30 : 0,
    whiteSpace: 'pre-wrap',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'center',
    fontStyle: isDeleted ? 'italic' : 'normal',
    alignItems: 'center',
    '& p': {
      margin: ({ isMessage }) => !isMessage ? '0 0 0 5px' : 0,
      width: ({ isMessage }) => !isMessage ? 'unset' : '100%',
    },
  }),
  buttons: ({ isCreator }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: isCreator ? 'end' : 'start',
    marginLeft: isCreator ? 30 : 0,
    marginRight: isCreator ? 30 : 0,
  }),
  light: {
    color: '#707070',
  },
  delete: {
    border: '0 none',
    background: 'transparent',
    color: theme.error,
    fontSize: 13,
    padding: 0,
  },
}), { name: 'TodoMessage' })

const TodoMessage = (props) => {
  const { message: messageProp, className, todo, onDelete, readOnly } = props
  const message = messageProp || {}

  const { creator, message: content, type: messageType, creatorId } = message
  const isCreator = user.userId === creatorId

  const { type: todoType } = todo

  const isDeleted = message.deletedAt !== null
  const isDone = content === TodoStatusEnum.DONE
  const isSigningStatus = content.includes('unterschrieben')

  const isMessage = messageType === TodoHistoryTypes.MESSAGE
  const statusMessage = isDeleted
    ? lang.todo.detail.status.deleted
    : lang.todo.detail.status[isDone ? todoType : 'rejected']

  const displayMessage = () => {
    if ((isSigningStatus) || (isMessage && !isDeleted)) return content
    if (!isMessage || isDeleted) return statusMessage
  }

  const classes = useStyles({ isCreator, isMessage, isDeleted })
  const messageIcon = useMemo(() => {
    if (isMessage) { return null }

    let icon = CloseIcon
    let fill = theme.red
    if (isSigningStatus) {
      icon = SigningIcon
      fill = theme.paleBlue
    }
    if (isDone) {
      icon = CheckIcon
      fill = theme.green
    }

    return <SVGIcon fill={fill} svg={icon} height="18px" width="18px" />
  }, [isDone, isMessage, isSigningStatus])

  const creatorName = useMemo(() => {
    if (!creator) { return '' }
    return buildName(creator)
  }, [creator])

  if (!messageProp) {
    return null
  }

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.container}>
        <div className={clsx(classes.meta, classes.light)}>
          <Avatar kind="name" salutation={creatorName} width={'20px'} className={classes.avatar} />
          {creator && `${creatorName} | `} {moment(message.createdAt).format(config.datetimeFormat.dateTime24)}{' '}
          {lang.generics.hour}
        </div>
        <div className={classes.message}>
          {messageIcon}
          <p>{displayMessage()}</p>
        </div>
        {isCreator && isMessage && !isDeleted && !readOnly && (
          <div className={classes.buttons}>
            <button onClick={() => onDelete(todo.id, message.id)} className={classes.delete}>
              <SVGIcon svg={DeleteIcon} fill={theme.error} width="10px" height="10px" />{' '}
              {lang.generics.delete}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

TodoMessage.propTypes = {
  message: PropTypes.object.isRequired,
  className: PropTypes.string,
  readOnly: PropTypes.bool,
}

TodoMessage.defaultProps = {
  className: null,
  readOnly: false,
}

export default TodoMessage
